import { Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import {
    IdleTimeoutService,
    IdleWarningModalComponent
} from '@libs/idle-timeout';
import { Subject, of } from 'rxjs';
import { catchError, filter, take, takeUntil } from 'rxjs/operators';
import { AuthenticationService } from './authentication.service';
import { LocalStorageService } from './local-storage.service';
import { sanitizeRedirectUrl } from '../utils/safe-redirect.util';
import { MixpanelService } from './mixpanel-service.service';

const PUBLIC_ROUTE_PREFIXES = [
    '/login',
    '/forgot-password',
    '/reset-password',
    '/welcome-to-cep',
    '/sso_service',
    '/loginasadmin',
    '/redirect-url',
    '/cep-authorize'
];

@Injectable({
    providedIn: 'root'
})
export class IdleSessionService implements OnDestroy {
    private readonly destroy$ = new Subject<void>();
    private isInitialized = false;
    private isLoggingOut = false;
    private warningModalRef: NgbModalRef | null = null;

    constructor(
        private idleTimeoutService: IdleTimeoutService,
        private localStorageService: LocalStorageService,
        private authenticationService: AuthenticationService,
        private router: Router,
        private modalService: NgbModal,
        private mixpanelService: MixpanelService
    ) {}

    initialize(): void {
        if (this.isInitialized) {
            return;
        }
        this.isInitialized = true;
        this.syncWithRoute();
        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                takeUntil(this.destroy$)
            )
            .subscribe(() => this.syncWithRoute());
        this.idleTimeoutService.warning$
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.openWarning());
        this.idleTimeoutService.timeout$
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.logoutDueToIdle());
        this.idleTimeoutService.warningDismissed$
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.closeWarning());
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.closeWarning();
        this.idleTimeoutService.stop();
    }

    private syncWithRoute(): void {
        const hasSession = this.localStorageService.checkSession();
        if (hasSession && !this.isPublicRoute(this.router.url)) {
            this.isLoggingOut = false;
            this.idleTimeoutService.start();
            return;
        }
        this.closeWarning();
        this.idleTimeoutService.stop();
    }

    private isPublicRoute(url: string): boolean {
        const path = url.split('?')[0];
        return PUBLIC_ROUTE_PREFIXES.some(
            (prefix) => path === prefix || path.startsWith(`${prefix}/`)
        );
    }

    private openWarning(): void {
        if (this.warningModalRef) {
            return;
        }
        this.warningModalRef = this.modalService.open(
            IdleWarningModalComponent,
            {
                centered: true,
                backdrop: 'static',
                keyboard: false
            }
        );
        const instance = this.warningModalRef
            .componentInstance as IdleWarningModalComponent;
        instance.stay
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe(() => this.keepSession());
        instance.logout
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe(() => this.logoutDueToIdle());
    }

    private keepSession(): void {
        this.closeWarning();
        this.idleTimeoutService.stayLoggedIn();
    }

    private closeWarning(): void {
        if (!this.warningModalRef) {
            return;
        }
        try {
            this.warningModalRef.close();
        } catch {
            // already dismissed
        }
        this.warningModalRef = null;
    }

    private logoutDueToIdle(): void {
        if (this.isLoggingOut) {
            return;
        }
        this.isLoggingOut = true;
        this.closeWarning();
        this.idleTimeoutService.notifyLogout();
        const sessionUid = this.localStorageService.get('session_uid');
        const isLoginByAdmin =
            this.localStorageService.get('userData')?.is_loginByAdmin;
        // Sanitized on read: a payload persisted to returnURL by the
        // login-as-admin XSS would otherwise fire on idle logout.
        const returnURL = sanitizeRedirectUrl(
            this.localStorageService.get('returnURL')
        );
        this.authenticationService
            .Logout()
            .pipe(catchError(() => of(null)))
            .subscribe((response) =>
                this.finishLogout(
                    response,
                    sessionUid,
                    isLoginByAdmin,
                    returnURL
                )
            );
    }

    private finishLogout(
        response: { data?: { logout_url?: string } } | null,
        sessionUid: unknown,
        isLoginByAdmin: unknown,
        returnURL: string
    ): void {
        this.mixpanelService.track('Logout action', {});
        this.authenticationService.clearSession();
        if (sessionUid) {
            this.localStorageService.set('session_uid', sessionUid);
        }
        if (response?.data?.logout_url) {
            window.location.href = response.data.logout_url;
            return;
        }
        if (isLoginByAdmin) {
            window.close();
            window.location.href = returnURL;
            return;
        }
        this.router.navigate(['/login']);
    }
}
